import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { compareDesc } from 'date-fns';

import { buildURL, post } from '@/shared/fetch';

import { CursorPagination } from '../types';
import { getInfiniteQueryFn, getQueryFn } from '../utils';
import { BellNotification } from './types';

export const readAllNotificationsMutationFn = () => {
  return post(buildURL('/notifications/viewed'));
};

export const readNotificationMutationFn = (notificationId: string) => {
  return post(buildURL(`/notifications/${notificationId}/viewed`));
};

export const getUnreadNotificationCountQueryOption = () =>
  queryOptions({
    queryKey: ['notifications', 'unread'],
    queryFn: getQueryFn<{ unread: number }>('/notifications/unread'),
    select: ({ unread }) => unread,
  });

/**
 * The pinned strip, drawn above everything else in the bell.
 *
 * Its own request rather than the front of the list below, because that list paginates on an `_id`
 * keyset cursor: sorting pinned rows to the front would put a row with a small `_id` on page one
 * *and* leave it after the cursor, so page two would serve it again.
 *
 * Its unread rows are still counted by `/notifications/unread`, so nothing that reads this query
 * should add them to the badge a second time.
 */
export const getPinnedNotificationsQueryOptions = () =>
  queryOptions({
    queryKey: ['notifications', 'pinned'],
    queryFn: getQueryFn<CursorPagination<BellNotification>>('/notifications', { pinned: '1' }),
  });

export const getNotificationsInfiniteQueryOptions = () =>
  infiniteQueryOptions({
    queryKey: ['notifications', 'list'],
    // `pinned=0` — the strip is fetched separately, and asking for both here would draw every
    // pinned notification twice.
    queryFn: getInfiniteQueryFn<CursorPagination<BellNotification>>('/notifications', { pinned: '0' }),
    initialPageParam: '',
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    select: ({ pages }) =>
      pages.length > 0
        ? ({
            list: pages
              .flatMap((page) => page.list)
              .sort((a, b) => compareDesc(new Date(a.createdAt), new Date(b.createdAt))),
            nextCursor: pages[pages.length - 1].nextCursor,
          } as CursorPagination<BellNotification>)
        : undefined,
  });
